import STORYLINES from './storylinesDatabase';
import { TITLES_DATABASE } from './titlesDatabase';
import { getWrestlerName } from './wrestlerUtils';
import React, { useState, useRef } from 'react';
import { ShowRecapPopup, MatchResultModal, ShowClosingScreen, PostShowData, PLECommonMatchResult, PromoChangeCard, TitleChangeCard, RivalryCreatedCard } from './ShowResults';

// Helper: pick a new variant for a level if not already set
function ensureVariantForLevel(rivalry, level) {
  if (!rivalry.storylineId) return rivalry;
  var existingMap = rivalry.storylineVariantByLevel || {};
  if (typeof existingMap[level] === 'number') return rivalry;
  var s = STORYLINES.find(function(x) { return x.id === rivalry.storylineId; });
  if (!s) return rivalry;
  var phase = s.phases.find(function(p) { return p.level === level; });
  if (!phase || !phase.variants || phase.variants.length === 0) return rivalry;
  var newMap = { ...existingMap };
  newMap[level] = Math.floor(Math.random() * phase.variants.length);
  return { ...rivalry, storylineVariantByLevel: newMap };
}

// ── ShowFlow ─────────────────────────────────────────────────────────
// Orchestrates the full 4-step show results flow
// Steps: recap → results (per segment) → closing → post-show → advance week
export default function ShowFlow({ gameData, brandColor, brandBg, onClose, onFinishShow }) {
  const slots = (gameData && gameData.bookingSlots) || [];
  const isPLEWeekEarly = !!(gameData && gameData.week && gameData.week % 5 === 0);
  // Exclude interbrand opener/main_event slots for PLE weeks — handled by PLECommonMatchResult
  const bookedSlots = slots.filter(s => {
    if (!s.booking || !s.booking.participants || s.booking.participants.length === 0) return false;
    if (isPLEWeekEarly && (s.type === 'opener' || s.type === 'main_event' || s.isInterBrand)) return false;
    return true;
  });

  const [step, setStep] = useState('recap'); // recap | results | closing | postshow
  const [currentIdx, setCurrentIdx] = useState(0);
  const [results, setResults] = useState([]); // one entry per slot
  const isPLEWeek = !!(gameData && gameData.week && gameData.week % 5 === 0);
  const [showPromoChange, setShowPromoChange] = useState(null);
  const [showTitleChange, setShowTitleChange] = useState(null); // result with titleChange
  const [showRivalryCreated, setShowRivalryCreated] = useState(null); // newly created rivalry
  const [pleStep, setPLEStep] = useState('opener');
  const [pleCommonResults, setPLECommonResults] = useState({});

  const currentSlot = bookedSlots[currentIdx];

  const handleResultSave = (result) => {
    const newResults = [...results];
    newResults[currentIdx] = { ...result, slotIdx: slots.indexOf(currentSlot), isPromo: currentSlot.type === 'promo', participants: result.participants || (currentSlot.booking && currentSlot.booking.participants) || [], matchRule: currentSlot.booking && currentSlot.booking.matchRule };
    setResults(newResults);

    // Show promo change card for role-change / class-change (current slot is the promo itself)
    var promoAction = currentSlot && currentSlot.booking && currentSlot.booking.promoAction;
    if (currentSlot.type === 'promo' && (promoAction === 'role-change' || promoAction === 'class-change')) {
      setShowPromoChange(currentSlot);
      return;
    }

    // Show title change animation — BEFORE checking next slot so it's never skipped
    if (result.titleChange && result.newChampion) {
      setShowTitleChange(result);
      return;
    }

    // Check if NEXT slot is a role-change / class-change promo → auto-register and show animation directly
    var nextIdx = currentIdx + 1;
    var nextSlot = bookedSlots[nextIdx];
    if (nextSlot) {
      var nextPromoAction = nextSlot.booking && nextSlot.booking.promoAction;
      if (nextSlot.type === 'promo' && (nextPromoAction === 'role-change' || nextPromoAction === 'class-change')) {
        var autoPromoResult = {
          slotIdx: slots.indexOf(nextSlot),
          isPromo: true,
          participants: (nextSlot.booking && nextSlot.booking.participants) || [],
          matchRule: null,
          skipped: false,
          notes: '',
        };
        newResults[nextIdx] = autoPromoResult;
        setResults(newResults);
        setCurrentIdx(nextIdx);
        setShowPromoChange(nextSlot);
        return;
      }
    }

    // Show rivalry created animation
    if (result.rivalryImpact === 'create') {
      var newRiv = null;
      var rivs = (gameData && gameData.rivalries) || [];
      // Find the most recently created rivalry containing these participants
      var pIds = (result.participants || []).filter(Boolean).map(function(p) { return p.wrestler_id; });
      newRiv = rivs.find(function(r) {
        return pIds.some(function(id) {
          return (r.side1 || []).some(function(x) { return String(x) === String(id); }) ||
                 (r.side2 || []).some(function(x) { return String(x) === String(id); });
        });
      });
      // Build a preview rivalry from the result data
      if (!newRiv && result.rivalrySide1 && result.rivalrySide1.length > 0) {
        newRiv = {
          level: 1,
          type: result.newRivalryType === '2v2' ? 'team' : 'solo',
          side1: result.rivalrySide1.map(function(w) { return w.wrestler_id; }),
          side2: result.rivalrySide2.map(function(w) { return w.wrestler_id; }),
        };
      }
      if (newRiv) {
        setShowRivalryCreated(newRiv);
        return;
      }
    }

    advanceAfterResult();
  };

  const isTransitioning = useRef(false);

  var advanceAfterResult = function() {
    if (isTransitioning.current) return;
    isTransitioning.current = true;
    setTimeout(function() { isTransitioning.current = false; }, 400);

    if (currentIdx < bookedSlots.length - 1) {
      setCurrentIdx(currentIdx + 1);
    } else if (isPLEWeek && pleStep === 'midcard') {
      setCurrentIdx(bookedSlots.length);
    } else {
      setStep('closing');
    }
  };

  var _dummy = function() {
    if (currentIdx < bookedSlots.length - 1) {
      setCurrentIdx(currentIdx + 1);
    } else if (isPLEWeek && pleStep === 'midcard') {
      // PLE: after last midcard -> show main event button screen
      setCurrentIdx(bookedSlots.length); // triggers midcardDone
    } else {
      setStep('closing');
    }
  };
  _dummy = null;

  const handlePromoChangeContinue = () => {
    setShowPromoChange(null);
    advanceAfterResult();
  };

  const handleTitleChangeContinue = () => {
    setShowTitleChange(null);
    // After title change animation, check if next slot is a role/class-change promo
    var nextIdx = currentIdx + 1;
    var nextSlot = bookedSlots[nextIdx];
    if (nextSlot) {
      var nextPromoAction = nextSlot.booking && nextSlot.booking.promoAction;
      if (nextSlot.type === 'promo' && (nextPromoAction === 'role-change' || nextPromoAction === 'class-change')) {
        var newResults = [...results];
        newResults[nextIdx] = {
          slotIdx: slots.indexOf(nextSlot),
          isPromo: true,
          participants: (nextSlot.booking && nextSlot.booking.participants) || [],
          matchRule: null,
          skipped: false,
          notes: '',
        };
        setResults(newResults);
        setCurrentIdx(nextIdx);
        setShowPromoChange(nextSlot);
        return;
      }
    }
    advanceAfterResult();
  };

  const handleBack = () => {
    if (currentIdx > 0) setCurrentIdx(currentIdx - 1);
  };

  const handleSkip = () => {
    const newResults = [...results];
    newResults[currentIdx] = { skipped: true, slotIdx: slots.indexOf(currentSlot), isPromo: currentSlot.type === 'promo' };
    setResults(newResults);
    advanceAfterResult();
  };

  const handleAdvanceWeek = ({ finalBudget, finalFans, rivalFans }) => {
    // Apply title changes
    var championsRaw = (gameData && gameData.champions) ? { ...gameData.champions } : {};
    var champMap = { ...(championsRaw.champions || {}) };
    var titleHistory = [...((gameData && gameData.titleHistory) || [])];
    const currentWeek = (gameData && gameData.week) || 1;
    const currentSeason = (gameData && gameData.season) || 1;

    results.forEach(r => {
      if (r && r.titleChange && r.titleSlotKey && r.newChampion) {
        // Close old reign
        var openIdx = titleHistory.findIndex(th => th.titleKey === r.titleSlotKey && !th.endWeek);
        if (openIdx !== -1) {
          titleHistory[openIdx] = { ...titleHistory[openIdx], endWeek: currentWeek, endSeason: currentSeason };
        }

        // Find co-champion (tag partner of winner)
        var matchRule = r.matchRule || (r.participants && r.participants.length === 4 ? 'tag' : '1v1');
        var coChamps = [r.newChampion];

        var TEAM_SIZES_MAP = {
          'tag': [2,2], 'tag-triple-threat': [2,2,2], 'tag-fatal-4-way': [2,2,2,2],
          '3v3': [3,3], '4v4': [4,4],
          '1v2': [1,2], '1v3': [1,3], '2v3': [2,3],
        };
        var sizes = TEAM_SIZES_MAP[matchRule];
        if (sizes && r.participants && r.participants.length > 0) {
          // Group flat participants into teams
          var teams = [];
          var idx = 0;
          sizes.forEach(function(size) { teams.push(r.participants.slice(idx, idx + size)); idx += size; });

          // Find which team the winner is in
          var winnerTeam = teams.find(function(team) {
            return team.some(function(p) { return p && p.wrestler_id === r.newChampion.wrestler_id; });
          });

          if (winnerTeam && winnerTeam.length > 1) {
            // Partner is the other member of winner's team
            coChamps = winnerTeam.filter(function(p) { return p && p.wrestler_id; });
          } else {
            // Fallback: look in registered tagTeams
            var gamTagTeams = (gameData && gameData.tagTeams) || [];
            var registeredTeam = gamTagTeams.find(function(t) {
              return (t.partner1 && t.partner1.wrestler_id === r.newChampion.wrestler_id) ||
                     (t.partner2 && t.partner2.wrestler_id === r.newChampion.wrestler_id);
            });
            if (registeredTeam) {
              var p1 = registeredTeam.partner1;
              var p2 = registeredTeam.partner2;
              if (p1 && p2) coChamps = [p1, p2];
            }
          }
        }

        titleHistory.push({ titleKey: r.titleSlotKey, champions: coChamps.map(function(c) { return c.name || getWrestlerName(c, 'display'); }), startWeek: currentWeek, startSeason: currentSeason, endWeek: null, endSeason: null });
        champMap[r.titleSlotKey] = coChamps;
      }
    });

    championsRaw = { ...championsRaw, champions: champMap };

    // Apply rivalry changes
    var rivalries = [...((gameData && gameData.rivalries) || [])];
    var pastRivalries = [...((gameData && gameData.pastRivalries) || [])];

    // Helper: build event object for rivalry history
    var buildEvent = function(r, slot, eventType) {
      var p = (slot && slot.booking && slot.booking.participants) || [];
      var promoAction = (slot && slot.booking && slot.booking.promoAction) || null;
      var targets = (slot && slot.booking && slot.booking.promoTargets) || [];
      var matchType = (slot && slot.booking && slot.booking.matchType) || 'normal';
      var matchRule = (slot && slot.booking && slot.booking.matchRule) || '1v1';
      var titleMatch = (slot && slot.booking && slot.booking.titleMatch) || null;

      // Pick a fresh random variant only for meaningful events (not maintain)
      var eventLevel = r.rivalryImpact === 'end' ? 5 : (r.level || 1);
      var lastIdx = r.storylineVariantByLevel ? r.storylineVariantByLevel[eventLevel] : null;
      var freshVariantIdx = null;
      if (r.storylineId && r.rivalryImpact !== 'maintain') {
        var stForEvent = STORYLINES.find(function(s) { return s.id === r.storylineId; });
        if (stForEvent) {
          var phaseForEvent = stForEvent.phases.find(function(ph) { return ph.level === eventLevel; });
          if (phaseForEvent && phaseForEvent.variants && phaseForEvent.variants.length > 1) {
            var available = phaseForEvent.variants.map(function(_, i) { return i; }).filter(function(i) { return i !== lastIdx; });
            freshVariantIdx = available[Math.floor(Math.random() * available.length)];
          } else if (phaseForEvent && phaseForEvent.variants && phaseForEvent.variants.length === 1) {
            freshVariantIdx = 0;
          }
        }
      }

      return {
        week: currentWeek,
        type: eventType, // 'match' | 'promo' | 'create'
        impact: r.rivalryImpact, // 'create' | 'heatup' | 'cooldown' | 'end' | 'maintain'
        rivalryLevel: eventLevel,
        storylineId: r.storylineId || null,
        storylineVariantIdx: freshVariantIdx !== null ? freshVariantIdx : lastIdx,
        // Match info
        participants: p.filter(Boolean).map(function(w) { return { wrestler_id: w.wrestler_id, name: getWrestlerName(w, 'display') }; }),
        winner: r.winner ? { wrestler_id: r.winner.wrestler_id, name: getWrestlerName(r.winner, 'display') } : null,
        noContest: r.noContest || false,
        rating: r.rating || 0,
        matchType: matchType,
        matchRule: matchRule,
        titleMatch: titleMatch,
        titleChange: r.titleChange || false,
        // Promo info
        isPromo: r.isPromo || false,
        promoAction: promoAction,
        callers: (r.callers || []).filter(Boolean).map(function(w) { return { wrestler_id: w.wrestler_id, name: getWrestlerName(w, 'display') }; }),
        targets: (r.targets || targets || []).filter(Boolean).map(function(w) { return { wrestler_id: w.wrestler_id, name: getWrestlerName(w, 'display') }; }),
        // Notes
        notes: r.notes || '',
      };
    };

    // Helper: find rivalry by participants
    var findRivalryIdx = function(participants) {
      var pIds = participants.map(function(w) { return w && w.wrestler_id; }).filter(Boolean);
      return rivalries.findIndex(function(rv) {
        var allIds = (rv.side1 || []).concat(rv.side2 || []);
        return pIds.some(function(id) { return allIds.includes(id); });
      });
    };

    results.forEach(r => {
      if (!r) return;
      var slot = slots[r.slotIdx];
      if (!slot || !slot.booking) return;
      var p = slot.booking.participants || [];

      var promoActionRiv = (slots[r.slotIdx] && slots[r.slotIdx].booking && slots[r.slotIdx].booking.promoAction) || null;
      var isCallOutPromo = r.isPromo && promoActionRiv && (promoActionRiv === 'call-out' || promoActionRiv === 'tag-team-call-out');
      var eventType = r.isPromo ? 'promo' : 'match';

      // ── Step 1: Auto-record event in ALL rivalries involving these participants ──
      var pIds = p.filter(Boolean).map(function(w) { return w.wrestler_id; });
      // Also include interference wrestlers as "present" for rivalry matching
      var interferenceA = slot.booking && slot.booking.interferenceA;
      var interferenceB = slot.booking && slot.booking.interferenceB;
      if (interferenceA && interferenceA.wrestler_id) pIds = pIds.concat([interferenceA.wrestler_id]);
      if (interferenceB && interferenceB.wrestler_id && interferenceB !== 'any') pIds = pIds.concat([interferenceB.wrestler_id]);
      rivalries.forEach(function(rv, rvIdx) {
        var side1Ids = (rv.side1 || []).map(String);
        var side2Ids = (rv.side2 || []).map(String);
        var pIdsStr = pIds.map(String);
        // Both sides of the rivalry must be represented — avoids solo promos being logged
        var side1Present = pIdsStr.some(function(id) { return side1Ids.includes(id); });
        var side2Present = pIdsStr.some(function(id) { return side2Ids.includes(id); });
        if (!side1Present || !side2Present) return;
        // Only auto-record if no explicit action was set for this rivalry (avoid double)
        var hasExplicitAction = r.rivalryActions && r.rivalryActions[rv.id];
        if (hasExplicitAction) return;
        var currentRv = rivalries[rvIdx];
        var ev = buildEvent({ ...r, rivalryImpact: 'maintain', level: currentRv.level, storylineId: currentRv.storylineId, storylineVariantByLevel: currentRv.storylineVariantByLevel }, slot, eventType);
        rivalries[rvIdx] = { ...rivalries[rvIdx], weeks: (rivalries[rvIdx].weeks || 0) + 1, events: [...(rivalries[rvIdx].events || []), ev] };
      });

      // ── Step 2: Handle explicit multi-rivalry actions ──
      var handledByActions = false;
      if (r.rivalryActions && Object.keys(r.rivalryActions).length > 0) {
        handledByActions = true;
        Object.keys(r.rivalryActions).forEach(function(rvId) {
          var action = r.rivalryActions[rvId];
          var rvIdx = rivalries.findIndex(function(rv) { return String(rv.id) === String(rvId); });
          if (rvIdx === -1) return;
          // Pass current rivalry state so event captures level BEFORE the change
          var currentRivalry = rivalries[rvIdx];
          var ev = buildEvent({ ...r, rivalryImpact: action, level: currentRivalry.level, storylineId: currentRivalry.storylineId, storylineVariantByLevel: currentRivalry.storylineVariantByLevel }, slot, eventType);
          // Update the event we already auto-recorded with the correct impact
          var existingEvents = rivalries[rvIdx].events || [];
          // Replace last event ONLY if it's the auto-recorded 'maintain' event from Step 1
          // Never replace a 'create' event (e.g. rivalry created this same show then immediately heatup'd)
          var lastEv = existingEvents[existingEvents.length - 1];
          var updatedEvents = (lastEv && lastEv.impact === 'maintain')
            ? existingEvents.slice(0, -1).concat([{ ...ev }])
            : existingEvents.concat([{ ...ev }]);
          if (action === 'heatup') {
            var newLvl = Math.min(4, (rivalries[rvIdx].level || 1) + 1);
            var heatedRivalry = { ...rivalries[rvIdx], level: newLvl, events: updatedEvents };
            rivalries[rvIdx] = ensureVariantForLevel(heatedRivalry, newLvl);
          } else if (action === 'heatup2') {
            var newLvl2h = Math.min(4, (rivalries[rvIdx].level || 1) + 2);
            var heatedRivalry2 = { ...rivalries[rvIdx], level: newLvl2h, events: updatedEvents };
            rivalries[rvIdx] = ensureVariantForLevel(heatedRivalry2, newLvl2h);
          } else if (action === 'cooldown') {
            var nl = Math.max(1, (rivalries[rvIdx].level || 1) - 1);
            var cooledRivalry = { ...rivalries[rvIdx], level: nl, events: updatedEvents };
            rivalries[rvIdx] = ensureVariantForLevel(cooledRivalry, nl);
          } else if (action === 'maintain') {
            rivalries[rvIdx] = { ...rivalries[rvIdx], events: updatedEvents };
          } else if (action === 'end') {
            // Pick conclusion variant (level 5) before ending
            var endingRivalry = { ...rivalries[rvIdx], events: updatedEvents };
            var withConclusion = ensureVariantForLevel(endingRivalry, 5);
            var ended = { ...withConclusion, endWeek: currentWeek, ended: true, finalWinner: r.winner ? { wrestler_id: r.winner.wrestler_id, name: r.winner.name || getWrestlerName(r.winner, 'display') } : null };
            rivalries.splice(rvIdx, 1);
            pastRivalries.unshift(ended);
          }
        });
      }

      // ── Step 3: Handle pendingCreates ──
      if (r.pendingCreates && r.pendingCreates.length > 0) {
        r.pendingCreates.forEach(function(pc) {
          var isTeam = pc.type === '2v2';
          // Pick initial variant for level 1 FIRST so buildEvent can use it
          var initialVariantMap = {};
          if (pc.storylineId) {
            var sFound = STORYLINES.find(function(s) { return s.id === pc.storylineId; });
            if (sFound) {
              var phase1 = sFound.phases.find(function(p) { return p.level === 1; });
              if (phase1 && phase1.variants && phase1.variants.length > 0) {
                initialVariantMap[1] = Math.floor(Math.random() * phase1.variants.length);
              }
            }
          }
          var ev = buildEvent({ ...r, rivalryImpact: 'create', level: 1, storylineId: pc.storylineId || null, storylineVariantByLevel: initialVariantMap }, slot, eventType);
          rivalries.push({
            id: Date.now() + Math.random(),
            type: isTeam ? 'team' : 'solo',
            level: 1,
            side1: pc.side1.map(function(w) { return w.wrestler_id; }),
            side2: pc.side2.map(function(w) { return w.wrestler_id; }),
            // Store snapshots so info persists even if wrestlers leave roster
            side1Snapshot: pc.side1.map(function(w) { return { wrestler_id: w.wrestler_id, name: getWrestlerName(w, 'display'), render: w.customRender || null, fight_class: w.fight_class, heel: w.heel, gender: w.gender }; }),
            side2Snapshot: pc.side2.map(function(w) { return { wrestler_id: w.wrestler_id, name: getWrestlerName(w, 'display'), render: w.customRender || null, fight_class: w.fight_class, heel: w.heel, gender: w.gender }; }),
            startWeek: currentWeek,
            weeks: 1,
            events: [ev],
            storylineId: pc.storylineId || null,
            storylineVariantByLevel: initialVariantMap,
          });
        });
      }

      // Skip old single-rivalry logic if handled by rivalryActions
      if (handledByActions) return;
      if (!r.rivalryImpact || r.rivalryImpact === 'maintain') return;

      if (!r.rivalryImpact || r.rivalryImpact === 'maintain') return;

      if (r.rivalryImpact === 'create' && (!r.isPromo || isCallOutPromo)) {
        var isTeam = r.newRivalryType === '2v2';
        var side1, side2;
        var side1Wrestlers = [], side2Wrestlers = [];
        if (r.rivalrySide1 && r.rivalrySide1.length > 0) {
          side1 = r.rivalrySide1.map(function(w) { return w.wrestler_id; });
          side2 = (r.rivalrySide2 || []).map(function(w) { return w.wrestler_id; });
          side1Wrestlers = r.rivalrySide1;
          side2Wrestlers = r.rivalrySide2 || [];
        } else if (r.callers && r.callers.length > 0 && r.targets && r.targets.length > 0) {
          side1Wrestlers = r.callers;
          side2Wrestlers = r.targets;
          side1 = r.callers.filter(Boolean).map(function(w) { return w.wrestler_id; });
          side2 = r.targets.filter(Boolean).map(function(w) { return w.wrestler_id; });
        } else {
          side1 = isTeam ? p.slice(0, 2).map(function(w) { return w && w.wrestler_id; }).filter(Boolean) : p.slice(0, 1).map(function(w) { return w && w.wrestler_id; }).filter(Boolean);
          side2 = isTeam ? p.slice(2, 4).map(function(w) { return w && w.wrestler_id; }).filter(Boolean) : p.slice(1, 2).map(function(w) { return w && w.wrestler_id; }).filter(Boolean);
        }
        var createEvent = buildEvent({ ...r, level: 1, storylineId: r.storylineId || null, storylineVariantByLevel: r.storylineVariantByLevel || {} }, slot, 'create');
        rivalries.push({
          id: Date.now() + Math.random(),
          type: isTeam ? 'team' : 'solo',
          level: 1,
          side1: side1,
          side2: side2,
            side1Snapshot: side1Wrestlers.map(function(w) { return { wrestler_id: w.wrestler_id, name: getWrestlerName(w, 'display'), render: w.customRender || null, fight_class: w.fight_class, heel: w.heel, gender: w.gender }; }),
            side2Snapshot: side2Wrestlers.map(function(w) { return { wrestler_id: w.wrestler_id, name: getWrestlerName(w, 'display'), render: w.customRender || null, fight_class: w.fight_class, heel: w.heel, gender: w.gender }; }),
          startWeek: currentWeek,
          weeks: 0,
          events: [createEvent],
        });
      } else if (r.rivalryImpact === 'heatup') {
        var existingIdx = findRivalryIdx(p);
        if (existingIdx !== -1) {
          var currentRiv = rivalries[existingIdx];
          var newLevel = Math.min(4, (currentRiv.level || 1) + 1);
          var ev = buildEvent({ ...r, level: currentRiv.level, storylineId: currentRiv.storylineId, storylineVariantByLevel: currentRiv.storylineVariantByLevel }, slot, eventType);
          var heatedRiv = {
            ...rivalries[existingIdx],
            level: newLevel,
            weeks: (rivalries[existingIdx].weeks || 0) + 1,
            events: [...(rivalries[existingIdx].events || []), ev],
          };
          rivalries[existingIdx] = ensureVariantForLevel(heatedRiv, newLevel);
        }
      } else if (r.rivalryImpact === 'cooldown') {
        var existingIdx2 = findRivalryIdx(p);
        if (existingIdx2 !== -1) {
          var currentRiv2 = rivalries[existingIdx2];
          var newLevel2 = Math.max(1, (currentRiv2.level || 1) - 1);
          var ev2 = buildEvent({ ...r, level: currentRiv2.level, storylineId: currentRiv2.storylineId, storylineVariantByLevel: currentRiv2.storylineVariantByLevel }, slot, eventType);
          var cooledRiv = {
            ...rivalries[existingIdx2],
            level: newLevel2,
            weeks: (rivalries[existingIdx2].weeks || 0) + 1,
            events: [...(rivalries[existingIdx2].events || []), ev2],
          };
          rivalries[existingIdx2] = ensureVariantForLevel(cooledRiv, newLevel2);
        }
      } else if (r.rivalryImpact === 'end') {
        var existingIdx3 = findRivalryIdx(p);
        if (existingIdx3 !== -1) {
          var currentRiv3 = rivalries[existingIdx3];
          var ev3 = buildEvent({ ...r, level: currentRiv3.level, storylineId: currentRiv3.storylineId, storylineVariantByLevel: currentRiv3.storylineVariantByLevel }, slot, eventType);
          var endingRiv = {
            ...rivalries[existingIdx3],
            events: [...(rivalries[existingIdx3].events || []), ev3],
          };
          var withConclusion = ensureVariantForLevel(endingRiv, 5);
          var ended = {
            ...withConclusion,
            endWeek: currentWeek,
            ended: true,
            finalWinner: r.winner ? { wrestler_id: r.winner.wrestler_id, name: r.winner.name || getWrestlerName(r.winner, 'display') } : null,
          };
          rivalries.splice(existingIdx3, 1);
          pastRivalries.unshift(ended);
        }
      } else if (r.rivalryImpact === 'maintain') {
        // Still log the event in the rivalry history
        var existingIdxM = findRivalryIdx(p);
        if (existingIdxM !== -1) {
          var currentRivM = rivalries[existingIdxM];
          var evM = buildEvent({ ...r, level: currentRivM.level, storylineId: currentRivM.storylineId, storylineVariantByLevel: currentRivM.storylineVariantByLevel }, slot, eventType);
          rivalries[existingIdxM] = {
            ...rivalries[existingIdxM],
            weeks: (rivalries[existingIdxM].weeks || 0) + 1,
            events: [...(rivalries[existingIdxM].events || []), evM],
          };
        }
      }
    });

    // Build show summary for lastWeek
    var lastWeek = results.filter(r => r && !r.isPromo && !r.skipped).map(r => {
      var slot = slots[r.slotIdx];
      var p = (slot && slot.booking && slot.booking.participants) || [];
      var mRule = (slot && slot.booking && slot.booking.matchRule) || '1v1';
      var winnerName = r.noContest ? 'No Contest' : (r.winner && (r.winner.name || getWrestlerName(r.winner, 'display'))) || '—';

      // Group participants by team
      var TAG_SIZES = { 'tag': [2,2], 'tag-triple-threat': [2,2,2], 'tag-fatal-4-way': [2,2,2,2], '3v3': [3,3], '4v4': [4,4], '1v2': [1,2], '1v3': [1,3], '2v3': [2,3] };
      var sizes = TAG_SIZES[mRule];
      var matchStr;
      var resultStr = winnerName;

      if (sizes && p.length > 0) {
        var validP = p.filter(Boolean);
        var teams = [];
        var off = 0;
        sizes.forEach(function(sz) { teams.push(validP.slice(off, off + sz)); off += sz; });
        matchStr = teams.map(function(team) {
          return team.map(function(w) { return w.name || getWrestlerName(w, 'display'); }).join(' & ');
        }).join(' vs ');
        // Find winner's team
        var winnerTeam = teams.find(function(team) {
          return team.some(function(w) { return (w.name || getWrestlerName(w, 'display')) === winnerName || (r.winner && w.wrestler_id === r.winner.wrestler_id); });
        });
        if (winnerTeam && winnerTeam.length > 1) {
          resultStr = winnerTeam.map(function(w) { return w.name || getWrestlerName(w, 'display'); }).join(' & ');
        }
      } else {
        matchStr = p.map(w => w && (w.name || getWrestlerName(w, 'display'))).filter(Boolean).join(' vs ');
      }

      return { match: matchStr, result: r.noContest ? 'No Contest' : resultStr, note: r.rating || null };
    });

    // Update rivals fans
    var updatedRivals = ((gameData && gameData.rivals) || []).map((rival, i) => {
      if (!rival) return rival;
      var rf = rivalFans && rivalFans[i];

      // If manual value entered, use it
      if (rf && rf.fans !== '' && rf.fans !== undefined) {
        return { ...rival, fans: Number(rf.fans) };
      }

      return rival;
    });

    // Apply promo automations (Role Change / Class Change)
    var updatedRoster = ((gameData && gameData.roster) || []).slice();

    // Remove wrestlers marked as releasing
    updatedRoster = updatedRoster.filter(function(w) { return !w.releasing; });

    // Vacate titles held by released wrestlers (those just removed from roster)
    var remainingIds = updatedRoster.map(function(w) { return w.wrestler_id; });
    var championsRawCleaned = (function() {
      var raw = championsRaw || {};
      var champMap = raw.champions || {};
      var newChampMap = {};
      Object.keys(champMap).forEach(function(key) {
        var champs = champMap[key];
        if (!champs || champs.length === 0) return;
        var stillValid = champs.every(function(c) { return c && remainingIds.includes(c.wrestler_id); });
        if (stillValid) newChampMap[key] = champs;
        // else: vacant — key omitted
      });
      return Object.assign({}, raw, { champions: newChampMap });
    })();

    // Apply injuries from match results
    results.forEach(function(r) {
      if (!r || r.skipped) return;
      var injs = r.injuries || [];
      injs.forEach(function(inj) {
        if (!inj || !inj.wrestler) return;
        var idx = updatedRoster.findIndex(function(w) { return w.wrestler_id === inj.wrestler.wrestler_id; });
        if (idx !== -1) {
          updatedRoster[idx] = { ...updatedRoster[idx], injured: true, injuryWeeksLeft: Number(inj.weeks) || 4 };
        }
      });
    });

    // Decrement injury countdown for wrestlers who were ALREADY injured BEFORE this show
    // This happens AFTER new injuries from this show are applied, so new injuries start at full duration
    updatedRoster = updatedRoster.map(function(w) {
      if (!w.injured) return w;
      // Check if this wrestler was just injured THIS show (injuryWeeksLeft hasn't been decremented yet)
      // We want to skip decrement for NEW injuries, only decrement EXISTING ones
      var wasNewlyInjured = results.some(function(r) {
        return (r.injuries || []).some(function(inj) {
          return inj && inj.wrestler && inj.wrestler.wrestler_id === w.wrestler_id;
        });
      });
      if (wasNewlyInjured) return w; // Don't decrement new injuries
      
      // Decrement existing injuries
      var newWeeks = (w.injuryWeeksLeft || 1) - 1;
      if (newWeeks <= 0) return { ...w, injured: false, injuryWeeksLeft: 0 };
      return { ...w, injuryWeeksLeft: newWeeks };
    });
    
    // Decrement suspension AND absence countdown each week
    updatedRoster = updatedRoster.map(function(w) {
      if (w.suspended) {
        var newWeeks = (w.suspensionWeeksLeft || 1) - 1;
        if (newWeeks <= 0) return { ...w, suspended: false, suspensionWeeksLeft: 0, suspensionReason: '', suspensionType: null };
        return { ...w, suspensionWeeksLeft: newWeeks };
      }
      if (w.absent) {
        var newWeeks = (w.suspensionWeeksLeft || 1) - 1;
        if (newWeeks <= 0) return { ...w, absent: false, suspensionWeeksLeft: 0, suspensionReason: '', suspensionType: null };
        return { ...w, suspensionWeeksLeft: newWeeks };
      }
      return w;
    });
    results.forEach(function(r) {
      if (!r || r.skipped || !r.isPromo) return;
      var slot = slots[r.slotIdx];
      var promoAction = slot && slot.booking && slot.booking.promoAction;
      var participants = (slot && slot.booking && slot.booking.participants) || [];
      if (!promoAction || participants.length === 0) return;

      participants.filter(Boolean).forEach(function(participant) {
        var rIdx = updatedRoster.findIndex(function(w) { return w.wrestler_id === participant.wrestler_id; });
        if (rIdx === -1) return;
        var w = updatedRoster[rIdx];

        if (promoAction === 'role-change') {
          // Flip heel/face
          updatedRoster[rIdx] = { ...w, heel: !w.heel };
        } else if (promoAction === 'class-change' && w.fight_class_secondary) {
          // Swap primary and secondary class
          updatedRoster[rIdx] = {
            ...w,
            fight_class: w.fight_class_secondary,
            fight_class_secondary: w.fight_class,
          };
        }
      });
    });

    // Advance week
    var newWeek = currentWeek + 1;
    var newSeason = currentSeason;
    var isEndOfSeason = newWeek > 50;
    if (isEndOfSeason) { newWeek = 50; } // keep at 50, EndOfSeasonFlow handles reset

    // Determine week type
    var weekType = 'regular';
    if (currentWeek % 5 === 0) weekType = currentWeek === 50 ? 'wrestlemania' : 'ple';
    else if (currentWeek % 5 === 4) weekType = 'gohome';

    // Compute show average rating
    var ratedResults = results.filter(function(r) { return r && r.rating > 0; });
    var avgRating = ratedResults.length > 0
      ? Math.round((ratedResults.reduce(function(s, r) { return s + r.rating; }, 0) / ratedResults.length) * 2) / 2
      : null;

    // Build week archive entry
    var pleCommonRes = isPLEWeek ? pleCommonResults : null;
    var profitsFromShow = finalBudget - ((gameData && gameData.budget) || 0);
    var showCost = ((gameData && gameData.bookingSlots) || []).reduce(function(t, s) { return t + ((s.booking && s.booking.cost) || 0); }, 0);
    // Snapshot rival fans AFTER update (uses values just entered by player)
    var rivalFansSnapshot = {};
    updatedRivals.filter(Boolean).forEach(function(r) {
      if (r.brand) rivalFansSnapshot[r.brand] = r.fans || 0;
    });

    var weekEntry = {
      week: currentWeek,
      season: currentSeason,
      weekType: weekType,
      ple: weekType === 'ple' || weekType === 'wrestlemania' ? (gameData.nextPle || null) : null,
      avgRating: avgRating,
      budget: finalBudget,
      fans: finalFans,
      profits: profitsFromShow,
      showCost: showCost,
      rivalFans: rivalFansSnapshot,
      titleChanges: results.filter(function(r) { return r && r.titleChange && r.newChampion; }).map(function(r) {
        var champs5 = champMap[r.titleSlotKey] || [r.newChampion];
        // champions.slots is an ARRAY — use .find(), not bracket access
        var champSlot5 = (championsRaw && Array.isArray(championsRaw.slots))
          ? championsRaw.slots.find(function(s) { return s.key === r.titleSlotKey; }) : null;
        var titleId5 = (champSlot5 && champSlot5.titleOverride && champSlot5.titleOverride.id) || r.titleSlotKey;
        var dbTitle5 = titleId5 ? (TITLES_DATABASE.find(function(t) { return t.id === titleId5; }) || null) : null;
        var tRender5 = (champSlot5 && champSlot5.titleOverride && champSlot5.titleOverride.render)
          || (titleId5 ? process.env.PUBLIC_URL + '/titles/' + titleId5 + '-render.png' : null);
        var tName5 = (champSlot5 && champSlot5.titleOverride && champSlot5.titleOverride.name)
          || (dbTitle5 && dbTitle5.name) || r.titleSlotKey || null;
        return {
          titleKey: r.titleSlotKey,
          titleRender: tRender5 || null,
          titleName: tName5,
          newChampion: r.newChampion && (r.newChampion.name || getWrestlerName(r.newChampion, 'display')),
          newChampions: champs5.map(function(c) { return c.name || getWrestlerName(c, 'display'); }),
        };
      }),
      pleResults: pleCommonRes,
      rivalryEvents: results.filter(function(r) { return r && r.rivalryImpact && r.rivalryImpact !== 'maintain' && r.rivalryImpact !== 'none'; }).map(function(r) {
        var side1Names = (r.rivalrySide1 || []).filter(Boolean).map(function(p) { return p.name || getWrestlerName(p, 'short') || '?'; });
        var side2Names = (r.rivalrySide2 || []).filter(Boolean).map(function(p) { return p.name || getWrestlerName(p, 'short') || '?'; });
        var callerNames = (r.callers || []).filter(Boolean).map(function(p) { return p.name || getWrestlerName(p, 'display') || '?'; });
        var targetNames = (r.targets || []).filter(Boolean).map(function(p) { return p.name || getWrestlerName(p, 'display') || '?'; });
        var names = (r.participants || []).filter(Boolean).map(function(p) { return p.name || getWrestlerName(p, 'display') || '?'; });
        var allNames = side1Names.length > 0
          ? [...side1Names, ...side2Names]
          : callerNames.length > 0 ? [...callerNames, ...targetNames] : names;
        return { impact: r.rivalryImpact, participants: allNames };
      }),
      promos: results.filter(function(r) { return r && r.isPromo && !r.skipped; }).map(function(r) {
        var slot = slots[r.slotIdx];
        var booking = slot && slot.booking;
        // callers = participants (from booking), targets = promoTargets
        var callers = (booking && booking.participants || []).filter(Boolean);
        var targets = (booking && booking.promoTargets || []).filter(Boolean);
        return {
          promoAction: (booking && booking.promoAction) || null,
          callers: callers.map(function(w) { return { wrestler_id: w.wrestler_id, name: getWrestlerName(w, 'display') }; }),
          targets: targets.map(function(w) { return { wrestler_id: w.wrestler_id, name: getWrestlerName(w, 'display') }; }),
          notes: r.notes || '',
        };
      }),
      matches: (function() {
        var TAG_RULE_SIZES_WE = {
          'tag': [2,2], '3v3': [3,3], '4v4': [4,4],
          'tag-triple-threat': [2,2,2], 'tag-fatal-4-way': [2,2,2,2],
          '1v2': [1,2], '1v3': [1,3], '2v3': [2,3],
        };

        // Helper: resolve any participant (brand sentinel or wrestler) to a display string
        var resolveP = function(p) {
          if (!p) return '?';
          if (p.isBrand) return p.brand_id || p.name || '?';
          return p.name || getWrestlerName(p, 'display') || '?';
        };

        // Mid-card matches (regular slots)
        var midMatches = results.filter(function(r) { return r && !r.isPromo && !r.skipped; }).map(function(r) {
          var slot = slots[r.slotIdx];
          var p = (slot && slot.booking && slot.booking.participants) || [];
          var mRule = (slot && slot.booking && slot.booking.matchRule) || '1v1';
          var winnerTeam = null;
          var winnerName = r.noContest ? null : (r.winner && (r.winner.name || getWrestlerName(r.winner, 'display'))) || null;
          var teamSizes = TAG_RULE_SIZES_WE[mRule];
          if (teamSizes && r.winner && p.length > 0) {
            var validP = p.filter(Boolean);
            var teams = [], off = 0;
            teamSizes.forEach(function(sz) { teams.push(validP.slice(off, off + sz)); off += sz; });
            var wTeam = teams.find(function(team) {
              return team.some(function(w) { return w && (w.name || getWrestlerName(w, 'display')) === winnerName || (r.winner && w.wrestler_id === r.winner.wrestler_id); });
            });
            if (wTeam && wTeam.length > 1) {
              winnerTeam = wTeam.map(function(w) { return w.name || getWrestlerName(w, 'display'); });
            }
          }
          var matchType3 = (slot && slot.booking && slot.booking.matchType) || 'normal';
          // champions.slots is an ARRAY — use .find(), not bracket access
          var champSlot3 = (championsRaw && Array.isArray(championsRaw.slots))
            ? championsRaw.slots.find(function(s) { return s.key === r.titleSlotKey; }) : null;
          var titleId3 = (champSlot3 && champSlot3.titleOverride && champSlot3.titleOverride.id) || r.titleSlotKey;
          var dbTitle3 = titleId3 ? (TITLES_DATABASE.find(function(t) { return t.id === titleId3; }) || null) : null;
          var tRender3 = (champSlot3 && champSlot3.titleOverride && champSlot3.titleOverride.render)
            || (titleId3 ? process.env.PUBLIC_URL + '/titles/' + titleId3 + '-render.png' : null);
          var tName3 = (champSlot3 && champSlot3.titleOverride && champSlot3.titleOverride.name)
            || (dbTitle3 && dbTitle3.name) || r.titleSlotKey || null;
          return {
            participants: p.filter(Boolean).map(resolveP),
            matchRule: mRule,
            matchType: matchType3 !== 'normal' ? matchType3 : null,
            winner: winnerName,
            winnerTeam: winnerTeam,
            noContest: !!r.noContest,
            rating: r.rating || null,
            titleKey: r.titleSlotKey || null,
            titleChange: !!r.titleChange,
            titleRender: r.titleSlotKey ? (tRender3 || null) : null,
            titleName: r.titleSlotKey ? (tName3 || null) : null,
            notes: r.notes || null,
          };
        });

        // PLE interbrand matches — inject opener first, main event last
        var openerMatch = null;
        var mainMatch = null;
        if (isPLEWeek && pleCommonRes) {
          var pleSlotsV2WE = (gameData && gameData.pleSlotsV2) || [];
          [{ key: 'opener', slotType: 'opener' }, { key: 'main', slotType: 'main_event' }].forEach(function(cfg) {
            var res = pleCommonRes[cfg.key];
            if (!res) return;
            // Get participants from pleSlotsV2
            var pleSlot = pleSlotsV2WE.find(function(s) { return s.type === cfg.slotType; });
            var p = (pleSlot && pleSlot.booking && pleSlot.booking.participants) || [];
            var mRule = (pleSlot && pleSlot.booking && pleSlot.booking.matchRule) || '1v1';
            // Winner: winnerTeam is an array of participant objects from PLECommonMatchResult
            var wTeamObjs = (res.winnerTeam && res.winnerTeam.length > 0) ? res.winnerTeam : null;
            var winnerNames = wTeamObjs ? wTeamObjs.map(resolveP).filter(Boolean) : [];
            var winnerStr = winnerNames.join(' & ') || (res.winnerBrand || null);
            var entry = {
              participants: p.filter(Boolean).map(resolveP),
              matchRule: mRule,
              matchType: null,
              winner: winnerStr,
              winnerTeam: winnerNames.length > 0 ? winnerNames : null,
              noContest: false,
              rating: res.rating || null,
              titleKey: null,
              titleChange: false,
              titleRender: null,
              titleName: null,
              notes: res.notes || null,
              slotType: cfg.slotType,
              isInterBrand: true,
            };
            if (cfg.key === 'opener') openerMatch = entry;
            else mainMatch = entry;
          });
        }

        // Final order: opener → mid-cards → main event
        var all = [];
        if (openerMatch) all.push(openerMatch);
        all = all.concat(midMatches);
        if (mainMatch) all.push(mainMatch);
        return all;
      })(),
    };

    var seasonHistory = [...((gameData && gameData.seasonHistory) || []), weekEntry];

    onFinishShow({
      ...gameData,
      week: newWeek,
      season: newSeason,
      weekType: weekType,
      _endOfSeason: isEndOfSeason,
      logisticsTokens: ((gameData && gameData.logisticsTokens) || 0) + 1,
      logistics: (gameData && gameData.logistics) || { arena: 1, crew: 1, sfx: 1, advertising: 1 },
      logisticsOwned: (gameData && gameData.logisticsOwned) || { arena: [1], crew: [1], sfx: [1], advertising: [1] },
      budget: finalBudget,
      fans: finalFans,
      roster: updatedRoster,
      rivals: updatedRivals,
      champions: championsRawCleaned,
      titleHistory,
      rivalries,
      pastRivalries,
      lastWeek,
      seasonHistory,
      bookingSlots: [],
      bookingFinished: false,
      showResults: { week: currentWeek, results, avgRating },
      _showWeekStart: true,
    });
  };

  // Show title change animation
  if (showTitleChange) {
    return (
      <TitleChangeCard
        result={showTitleChange}
        gameData={gameData}
        brandColor={brandColor}
        brandBg={brandBg}
        onContinue={handleTitleChangeContinue}
      />
    );
  }

  // Show rivalry created animation
  if (showRivalryCreated) {
    return (
      <RivalryCreatedCard
        rivalry={showRivalryCreated}
        roster={(gameData && gameData.roster) || []}
        brandColor={brandColor}
        brandBg={brandBg}
        onContinue={() => { setShowRivalryCreated(null); advanceAfterResult(); }}
      />
    );
  }

  // Show promo change animation
  if (showPromoChange) {
    return (
      <PromoChangeCard
        slot={showPromoChange}
        brandColor={brandColor}
        brandBg={brandBg}
        onContinue={handlePromoChangeContinue}
      />
    );
  }

  if (step === 'recap') {
    return <ShowRecapPopup gameData={gameData} brandColor={brandColor} brandBg={brandBg} onClose={onClose} onStart={() => { setStep('results'); if (isPLEWeek) setPLEStep('opener'); }} />;
  }

  // PLE Week — Opener first, then mid-card segments, then Main Event
  if (step === 'results' && isPLEWeek && (pleStep === 'opener' || pleStep === 'main')) {
    var isOpener2 = pleStep === 'opener';
    var rivals2 = ((gameData && gameData.rivals) || []).filter(Boolean);
    var BRAND_LOGOS_SF2 = {
      'Raw': process.env.PUBLIC_URL + '/brands/logos/RAW25_LOGO.png',
      'SmackDown': process.env.PUBLIC_URL + '/brands/logos/SMACKDOWN25_LOGO.png',
      'NXT': process.env.PUBLIC_URL + '/brands/logos/NXT_LOGO.png',
      'NXT 2.0': process.env.PUBLIC_URL + '/brands/logos/NXT20_LOGO.png',
      'WCW': process.env.PUBLIC_URL + '/brands/logos/WCW_LOGO.png',
      'ECW': process.env.PUBLIC_URL + '/brands/logos/ECW_LOGO.png',
      'NXT Mutiny': process.env.PUBLIC_URL + '/brands/logos/NXTMUTINY_LOGO.png',
    };
    var allBrands2 = [
      { name: gameData && gameData.brand, color: (gameData && gameData.brandColor) || '#d4af37', logo: (gameData && gameData.brandLogo) || BRAND_LOGOS_SF2[(gameData && gameData.brand)] || null },
    ].concat(rivals2.map(function(r) { return { name: r.brand, color: r.brandColor || r.color || '#444', logo: r.brandLogo || BRAND_LOGOS_SF2[r.brand] || null }; }));
    // Force WrestleMania from week 46
  var effectivePleId2 = (gameData && gameData.week >= 46) ? 'wrestlemania' : (gameData && gameData.nextPle);
  var pleSlotsV2_SF = (gameData && gameData.pleSlotsV2) || [];
  var activeSlotSF = pleSlotsV2_SF.find(function(s) { return s.type === (isOpener2 ? 'opener' : 'main_event'); });
  var pleBookingData2 = (gameData && gameData.pleBooking) || {};
  var myParticipants2 = (activeSlotSF && activeSlotSF.booking && activeSlotSF.booking.participants)
    || (isOpener2 ? (pleBookingData2.openerParticipants || []) : (pleBookingData2.mainEventParticipants || []));
  var myMatchRule2 = (activeSlotSF && activeSlotSF.booking && activeSlotSF.booking.matchRule) || '1v1';
    return (
      <PLECommonMatchResult
        key={pleStep}
        label={isOpener2 ? 'OPENER' : 'MAIN EVENT'}
        slotColor={isOpener2 ? '#2ecc71' : ((gameData && gameData.brandColor) || '#d4af37')}
        brands={allBrands2}
        myBrand={gameData && gameData.brand}
        myParticipants={myParticipants2}
        matchRule={myMatchRule2}
        brandColor={brandColor}
        onBack={isOpener2 ? null : function() { setPLEStep('midcard'); setCurrentIdx(0); }}
        onCancel={function() { if (onClose) onClose(); }}
        onSave={function(result) {
          setPLECommonResults(function(prev) { return Object.assign({}, prev, { [isOpener2 ? 'opener' : 'main']: result }); });
          if (isOpener2) {
            setPLEStep('midcard');
            setCurrentIdx(0);
          } else {
            setStep('closing');
          }
        }}
      />
    );
  }

  // PLE mid-card segments (between opener and main event)
  if (step === 'results' && isPLEWeek && pleStep === 'midcard') {
    var midcardDone = currentIdx >= bookedSlots.length;
    if (midcardDone) {
      setCurrentIdx(0);
      setPLEStep('main');
      return null;
    }
    if (currentSlot) {
      return (
        <MatchResultModal
          key={currentIdx}
          slot={currentSlot}
          gameData={gameData}
          brandColor={brandColor}
          slotIndex={currentIdx}
          onSave={handleResultSave}
          onSkip={handleSkip}
          onBack={handleBack}
          onCancel={onClose}
        />
      );
    }
    return null;
  }

  if (step === 'results' && currentSlot && !isPLEWeek) {
    return (
      <MatchResultModal
        key={currentIdx}
        slot={currentSlot}
        gameData={gameData}
        brandColor={brandColor}
        slotIndex={currentIdx}
        onSave={handleResultSave}
        onSkip={handleSkip}
        onBack={handleBack}
        onCancel={onClose}
      />
    );
  }

  if (step === 'closing') {
    return (
      <ShowClosingScreen
        gameData={gameData}
        results={results}
        pleCommonResults={pleCommonResults}
        isPLEWeek={isPLEWeek}
        brandColor={brandColor}
        brandBg={brandBg}
        onPostShow={() => setStep('postshow')}
        onReopen={() => { setStep('results'); setCurrentIdx(0); setResults([]); }}
      />
    );
  }

  if (step === 'postshow') {
    return (
      <PostShowData
        gameData={gameData}
        results={results}
        brandColor={brandColor}
        brandBg={brandBg}
        onAdvanceWeek={handleAdvanceWeek}
      />
    );
  }

  return null;
}
